<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";
import {
  countTodaysCards,
  updateUserGoal,
  getUserDailyGoal,
  getProgressBarValues,
  getDailyStreak,
  getAccuracyLast7Days,
  getAvgTimePerCardLast7Days,
  getNewWordsLearnedThisWeek,
  getUpcomingWorkload,
  getCompletionEstimate,
  getTotalAnsweredCards,
} from "@/Scripts/dbFunctions";
import { supabase } from "@/lib/supabase";

const userStore = useUserStore();
const router = useRouter();
const todaysCardCount = ref(0);
const showGoalModal = ref(false);
const goalValue = ref(0);
const activeLanguage = ref(null);
const flagEmoji = computed(() => {
  const map = { en: "🇺🇸", es: "🇪🇸", fr: "🇫🇷", de: "🇩🇪", zh: "🇨🇳", it: "🇮🇹" };
  return map[activeLanguage.value] || "🏳️";
});

// Progress bar config
const progressMax = 5000; // max value of the progress bar
const progressValues = ref({
  total_zeros: 0,
  total_ones: 0,
  total_twos: 0,
  total_threes: 0,
});
// Configurable colors for segments
const progressColors = ref({
  zeros: "#DCDCDC",
  ones: "#1E93AB",
  twos: "#44D281",
  threes: "#D28A44",
});

const progressSegments = computed(() => {
  const { total_zeros, total_ones, total_twos, total_threes } =
    progressValues.value;
  return [
    { key: "zeros", value: total_zeros, color: progressColors.value.zeros },
    { key: "ones", value: total_ones, color: progressColors.value.ones },
    { key: "twos", value: total_twos, color: progressColors.value.twos },
    {
      key: "threes",
      value: total_threes,
      color: progressColors.value.threes,
    },
  ];
});

const progressValue = computed(() => {
  const sum = progressSegments.value.reduce((s, x) => s + x.value, 0);
  return Math.min(progressMax, sum);
});

const progressBase = computed(() => {
  const sum = progressSegments.value.reduce((s, x) => s + x.value, 0);
  return sum > progressMax ? sum : progressMax;
});

// Additional stats state
const dailyStreak = ref(0);
const accuracy = ref({ correct: 0, total: 0, percent: 0 });
const avgTimeMs = ref(0);
const newWordsWeek = ref(0);
const upcoming = ref({ overdue: 0, days: [] });
const completion = ref({
  remaining_new: 0,
  avg_new_per_day: 0,
  days: null,
  eta: null,
});
const totalAnsweredAll = ref(0);

// Seconds with one decimal place
const avgTimeSec = computed(() => Math.round(avgTimeMs.value / 100) / 10);

const progressPercent = computed(() => {
  const pct = (progressValue.value / progressMax) * 100;
  return Math.floor(pct);
});

const openStat = (stat) => router.push(`/stats/${stat}`);

const updateGoal = async () => {
  const uid = userStore?.user?.id;
  if (!uid) {
    showGoalModal.value = false;
    return;
  }
  try {
    await updateUserGoal(uid, goalValue.value);
  } catch (e) {
    console.error("Failed to update goal:", e?.message || e);
  } finally {
    showGoalModal.value = false;
  }
};

onMounted(async () => {
  const uid = userStore?.user?.id;
  if (!uid) return;
  try {
    todaysCardCount.value = await countTodaysCards(uid);
  } catch (e) {
    console.error("Failed to fetch today's card count:", e?.message || e);
    todaysCardCount.value = 0;
  }

  // Load goal from user_preferences so other stats can react to the same ref
  try {
    goalValue.value = await getUserDailyGoal(uid);
  } catch (e) {
    console.error("Failed to fetch daily goal:", e?.message || e);
    goalValue.value = 0;
  }

  // Load progress bar values
  try {
    const vals = await getProgressBarValues(uid);
    progressValues.value = vals || {
      total_zeros: 0,
      total_ones: 0,
      total_twos: 0,
      total_threes: 0,
    };
  } catch (e) {
    console.error("Failed to fetch progress bar values:", e?.message || e);
    progressValues.value = {
      total_zeros: 0,
      total_ones: 0,
      total_twos: 0,
      total_threes: 0,
    };
  }

  // Fetch total answered cards (all time) via shared db function
  try {
    totalAnsweredAll.value = await getTotalAnsweredCards(uid);
  } catch (e) {
    console.error("Failed to fetch total answered cards:", e?.message || e);
    totalAnsweredAll.value = 0;
  }

  // Load additional stats
  try {
    const { getAvgReviewsPerCardLast7Days } = await import(
      "@/Scripts/dbFunctions"
    );
    const [streak, acc, avgMs, newW, upc, comp, revAvg] = await Promise.all([
      getDailyStreak(uid),
      getAccuracyLast7Days(uid),
      getAvgTimePerCardLast7Days(uid),
      getNewWordsLearnedThisWeek(uid),
      getUpcomingWorkload(uid, 7),
      getCompletionEstimate(uid, 14),
      getAvgReviewsPerCardLast7Days(uid),
    ]);
    dailyStreak.value = Number(streak) || 0;
    accuracy.value = acc || { correct: 0, total: 0, percent: 0 };
    avgTimeMs.value = Number(avgMs) || 0;
    newWordsWeek.value = Number(newW) || 0;
    upcoming.value = upc || { overdue: 0, days: [] };
    completion.value = {
      ...(comp || {
        remaining_new: 0,
        avg_new_per_day: 0,
        days: null,
        eta: null,
      }),
      avg_reviews_per_card: Number(revAvg) || 0,
    };
  } catch (e) {
    console.error("Failed to fetch additional stats:", e?.message || e);
  }
});
onMounted(async () => {
  try {
    const uid = userStore?.user?.id;
    if (!uid) return;
    const { data, error } = await supabase
      .from("user_preferences")
      .select("active_language")
      .eq("user_id", uid)
      .maybeSingle();
    if (!error && data) {
      activeLanguage.value = data.active_language || null;
    }
  } catch (e) {
    console.error("Failed to fetch active language:", e?.message || e);
  }
});
</script>

<template>
  <div class="main-content">
    <div class="home-view">
      <div v-if="activeLanguage" class="home-header-row">
        <div class="language-flag">
          <span class="flag-emoji">{{ flagEmoji }}</span>
        </div>
        <div class="beta-pill">Beta</div>
      </div>
      <div class="dashboard-grid">
        <div
          class="card my-progress clickable-card"
          role="link"
          tabindex="0"
          aria-label="Open My Progress details"
          @click="openStat('progress')"
          @keydown.enter="openStat('progress')"
          @keydown.space.prevent="openStat('progress')"
        >
          <h2>My Progress</h2>
          <div class="progress-subtext">
            {{ progressPercent }}% of {{ progressMax }}
          </div>
          <div class="progress-wrap">
            <div
              class="progress-track"
              role="meter"
              aria-label="Cards by state"
              aria-valuemin="0"
              :aria-valuemax="progressBase"
              :aria-valuenow="progressValue"
            >
              <div
                v-for="seg in progressSegments"
                :key="seg.key"
                class="progress-seg"
                :style="{
                  width: (seg.value / progressBase) * 100 + '%',
                  backgroundColor: seg.color,
                }"
              ></div>
            </div>
          </div>
        </div>
        <div
          class="card stat-card clickable-card"
          role="link"
          tabindex="0"
          aria-label="Open Upcoming Workload details"
          @click="openStat('workload')"
          @keydown.enter="openStat('workload')"
          @keydown.space.prevent="openStat('workload')"
        >
          <h2>Upcoming Workload</h2>
          <div class="stat-subtext" v-if="upcoming.overdue">
            Overdue: <strong>{{ upcoming.overdue }}</strong>
          </div>
          <div class="workload-bars">
            <div
              v-for="d in upcoming.days"
              :key="d.date"
              class="workload-bar"
              :title="d.date + ': ' + d.count"
            >
              <div
                class="workload-fill"
                :style="{ height: Math.min(36, d.count) + 'px' }"
              ></div>
              <div class="workload-label">{{ d.count }}</div>
            </div>
          </div>
        </div>

        <div
          class="card stat-card clickable-card"
          role="link"
          tabindex="0"
          aria-label="Open Today's Goal details"
          @click="openStat('today-goal')"
          @keydown.enter="openStat('today-goal')"
          @keydown.space.prevent="openStat('today-goal')"
        >
          <div class="card-header">
            <h2>Today's Goal</h2>
            <button
              class="link-button"
              @click.stop="showGoalModal = true"
              @keydown.enter.stop
              @keydown.space.stop
            >
              Change goal
            </button>
          </div>
          <div class="stat-value">
            <div
              :class="[
                'stat-badge',
                goalValue - todaysCardCount > 50
                  ? 'badge-red'
                  : goalValue - todaysCardCount > 0
                  ? 'badge-orange'
                  : 'badge-green',
              ]"
            >
              {{ goalValue - todaysCardCount }}
            </div>
            <span class="stat-unit">cards left today</span>
          </div>
        </div>

        <!-- New Stat Cards -->
        <div
          class="card stat-card clickable-card"
          role="link"
          tabindex="0"
          aria-label="Open Daily Streak details"
          @click="openStat('streak')"
          @keydown.enter="openStat('streak')"
          @keydown.space.prevent="openStat('streak')"
        >
          <h2>Daily Streak</h2>
          <div class="stat-value">
            <div
              :class="[
                'stat-badge',
                dailyStreak === 0
                  ? 'badge-red'
                  : dailyStreak <= 2
                  ? 'badge-orange'
                  : 'badge-green',
              ]"
            >
              {{ dailyStreak }}
            </div>
            <span class="stat-unit">days</span>
          </div>
        </div>

        <div
          class="card stat-card clickable-card"
          role="link"
          tabindex="0"
          aria-label="Open First-Try Accuracy details"
          @click="openStat('accuracy')"
          @keydown.enter="openStat('accuracy')"
          @keydown.space.prevent="openStat('accuracy')"
        >
          <div class="card-header">
            <h2>First-Try Accuracy</h2>
            <span class="chip">7 days</span>
          </div>
          <div class="stat-value">
            <div class="stat-badge">{{ accuracy.percent }}</div>
            <span class="stat-unit">%</span>
          </div>
        </div>

        <div
          class="card stat-card clickable-card"
          role="link"
          tabindex="0"
          aria-label="Open Average Time per Card details"
          @click="openStat('average-time')"
          @keydown.enter="openStat('average-time')"
          @keydown.space.prevent="openStat('average-time')"
        >
          <div class="card-header">
            <h2>Avg. Time per Card</h2>
            <span class="chip">7 days</span>
          </div>
          <div class="stat-value">
            <div class="stat-badge">{{ avgTimeSec }}</div>
            <span class="stat-unit">s</span>
          </div>
        </div>

        <div
          class="card stat-card clickable-card"
          role="link"
          tabindex="0"
          aria-label="Open Average Reviews per Card details"
          @click="openStat('reviews-per-card')"
          @keydown.enter="openStat('reviews-per-card')"
          @keydown.space.prevent="openStat('reviews-per-card')"
        >
          <div class="card-header">
            <h2>Avg Reviews per Card</h2>
            <span class="chip">7 days</span>
          </div>
          <div class="stat-value">
            <div class="stat-badge">
              {{ completion.avg_reviews_per_card || 0 }}
            </div>
            <span class="stat-unit">/card</span>
          </div>
        </div>

        <div
          class="card stat-card clickable-card"
          role="link"
          tabindex="0"
          aria-label="Open New Words details"
          @click="openStat('new-words')"
          @keydown.enter="openStat('new-words')"
          @keydown.space.prevent="openStat('new-words')"
        >
          <div class="card-header">
            <h2>New Words</h2>
            <span class="chip">7 days</span>
          </div>
          <div class="stat-value">
            <div class="stat-badge">{{ newWordsWeek }}</div>
            <span class="stat-unit">words</span>
          </div>
        </div>

        <div
          class="card stat-card clickable-card"
          role="link"
          tabindex="0"
          aria-label="Open Total Answered details"
          @click="openStat('total-answered')"
          @keydown.enter="openStat('total-answered')"
          @keydown.space.prevent="openStat('total-answered')"
        >
          <h2>Total Answered</h2>
          <div class="stat-value">
            <div class="stat-badge">{{ totalAnsweredAll }}</div>
            <span class="stat-unit">cards</span>
          </div>
        </div>

        <div
          class="card stat-card clickable-card"
          role="link"
          tabindex="0"
          aria-label="Open Completion Estimate details"
          @click="openStat('completion')"
          @keydown.enter="openStat('completion')"
          @keydown.space.prevent="openStat('completion')"
        >
          <h2>Completion Estimate</h2>
          <div class="stat-value">
            <div class="stat-badge">
              {{ completion.days == null ? "—" : completion.days }}
            </div>
            <span class="stat-unit">
              {{ completion.days == null ? "No recent pace" : "days" }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showGoalModal" class="modal-overlay">
      <div class="modal">
        <button class="close-button" @click="showGoalModal = false">×</button>
        <div class="modal-content">
          <label for="goalSlider">Daily goal: {{ goalValue }}</label>
          <input
            id="goalSlider"
            type="range"
            min="0"
            max="200"
            v-model.number="goalValue"
          />
        </div>
        <button class="update-button" @click="updateGoal">Update goal</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.main-content {
  /*margin-left: 75px; */
  padding: 20px; /* optional spacing */
  background: var(--app-bg);
  min-height: 100vh;
  position: relative;

  /* Layout variables for consistent header and grid alignment */
  --grid-left: 275px;
  --grid-right: 200px;
  --card-pad: 16px;
}

.language-flag {
  position: static;
  background: transparent;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

@media (max-width: 768px) {
  .home-header-row {
    margin-top: 15px;
    box-sizing: border-box;
  }
}
.flag-emoji {
  font-size: 40px;
  line-height: 1;
}
.dashboard-grid {
  margin-left: var(--grid-left);
  margin-right: var(--grid-right);
}

/* Two-column responsive grid */
.home-view .dashboard-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: minmax(120px, auto);
  gap: 16px;
}

/* Basic card styling */
.card {
  background: var(--sidebar-bg);
  border: none;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.clickable-card {
  cursor: pointer;
  transition: transform 140ms ease, box-shadow 140ms ease;
}

.clickable-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.1);
}

.clickable-card:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 3px;
}

/* Helper to span both columns (for future cards) */
.span-2 {
  grid-column: 1 / -1;
}

.my-progress h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 400;
}

.my-progress .progress-subtext {
  margin-top: 15px;
  font-size: 0.9rem;
  color: var(--text-color);
}

.progress-wrap {
  margin-top: 12px;
}

.progress-track {
  display: flex;
  align-items: stretch;
  height: 10px; /* thinner bar */
  background: var(--hover-bg);
  border-radius: 8px;
  overflow: hidden;
}

/* Stat cards */
.stat-card h2 {
  margin: 0 0 6px;
  font-size: 1.25rem;
  font-weight: 400;
}

.card h2 {
  font-weight: 400;
}

.stat-card .stat-value {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-color);
}

.stat-card .stat-subtext {
  margin-top: 10px;
  margin-bottom: 10px;
  font-size: 0.8rem;
  color: var(--text-color);
}

.sparkline {
  width: 100%;
  height: 48px;
  margin-top: 4px;
}

/* Numeric badge styling */
.stat-badge {
  display: inline-block;
  padding: 8px 12px;
  margin-top: 10px;
  margin-bottom: 5px;
  border-radius: 6px;
  background: var(--hover-bg);
  color: var(--text-color);
  line-height: 1;
  font-size: 1.5em; /* roughly twice as large as surrounding text */
}

/* Streak badge colors */
.badge-red {
  background: #fde2e2; /* pastel red */
}
.badge-orange {
  background: #ffe8cc; /* pastel orange */
}
.badge-green {
  background: #d4edda; /* pastel green */
}

.stat-unit {
  font-size: 1rem;
  font-weight: 400;
  color: var(--text-color);
}

.progress-seg {
  height: 100%;
}

.progress-seg + .progress-seg {
  box-shadow: inset 1px 0 0 rgba(0, 0, 0, 0.08); /* subtle separators */
}

/* Stat cards */
.stat-card h2 {
  margin: 0 0 6px;
  font-size: 1.25rem;
  font-weight: 400;
}

.stat-card .stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-color);
}

.stat-card .stat-subtext {
  margin-top: 2px;
  font-size: 0.8rem;
  color: var(--text-color);
}

.workload-bars {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  height: 48px;
  margin-top: 20px;
}

.workload-bar {
  width: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.workload-fill {
  width: 100%;
  background: var(--accent);
  border-radius: 2px 2px 0 0;
}

.workload-label {
  font-size: 0.7rem;
  color: var(--text-color);
  margin-top: 2px;
}

/* Text-only action button */
.link-button {
  background: none;
  border: none;
  padding: 0;
  color: var(--accent);
  font-weight: 500;
  cursor: pointer;
}

/* Header layout for title + action aligned right */
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* Small chip for time range */
.chip {
  display: inline-block;
  padding: 4px 8px;
  font-size: 0.8rem;
  background: var(--accent);
  color: #fff;
  border-radius: 999px;
  line-height: 1.2;
  font-weight: 600;
}

/* Modal overlay and popup */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  position: relative;
  width: 320px;
  height: 150px;
  background: var(--sidebar-bg);
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.close-button {
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}

.modal .modal-content {
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.update-button {
  position: absolute;
  right: 16px;
  bottom: 16px;
  background: var(--accent);
  color: #fff;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
}
input[type="range"]#goalSlider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  background: transparent;
  outline: none;
}
input[type="range"]#goalSlider::-webkit-slider-runnable-track {
  height: 6px;
  background: var(--accent);
  border-radius: 3px;
}
input[type="range"]#goalSlider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  background: #fff;
  border: 2px solid var(--accent);
  border-radius: 50%;
  cursor: pointer;
  margin-top: -6px; /* center thumb on 6px track */
}
input[type="range"]#goalSlider::-moz-range-track {
  height: 6px;
  background: var(--accent);
  border-radius: 3px;
}
input[type="range"]#goalSlider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  background: #fff;
  border: 2px solid var(--accent);
  border-radius: 50%;
  cursor: pointer;
}
@media (max-width: 768px) {
  /* Mobile: stack cards; spacing via CSS variables */
  .home-view .dashboard-grid {
    grid-template-columns: 1fr;
  }
  /* Extra breathing room at the bottom on mobile and set grid/padding variables */
  .main-content {
    padding-bottom: 50px;
    --grid-left: 16px;
    --grid-right: 16px;
    --card-pad: 16px;
  }
}
/* Header row to align flag left and Beta pill to right edge of stats cards */
.home-header-row {
  display: flex;
  align-items: center;
  margin-left: var(--grid-left);
  margin-right: var(--grid-right);
  margin-bottom: 2px;
  box-sizing: border-box;
}
.beta-pill {
  margin-left: auto;
  background: var(--accent);
  color: #fff;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 0.75rem;
  line-height: 1.2;
  font-weight: 700;
}
</style>
